import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { nocService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const NOCDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nocRequest, setNocRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingApproval, setProcessingApproval] = useState(false);

  useEffect(() => {
    fetchNOCDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchNOCDetail = async () => {
    try {
      const data = await nocService.getNOCRequestById(id);
      setNocRequest(data.nocRequest);
    } catch (error) {
      toast.error('Failed to fetch NOC details');
      navigate('/noc');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadNOC = async () => {
    try {
      const response = await api.get(`/noc/${id}/download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NOC_Certificate_${nocRequest?.student?.usn || 'certificate'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.message || 'Failed to download certificate');
    }
  };

  const handleApproval = async (status, remarks = '') => {
    setProcessingApproval(true);
    try {
      await nocService.processNOCApproval(id, status, remarks);
      toast.success(`NOC request ${status.toLowerCase()} successfully!`);
      fetchNOCDetail(); // Refresh the details
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process approval');
    } finally {
      setProcessingApproval(false);
    }
  };

  const getCurrentUserApproval = () => {
    if (!nocRequest || !user || user.role === 'STUDENT') return null;
    return nocRequest.approvals.find(approval => approval.approverRole === user.role);
  };

  const canModifyApproval = () => {
    const currentApproval = getCurrentUserApproval();
    if (!currentApproval) return false;
    // Can modify if status is PENDING or REJECTED (not APPROVED)
    return currentApproval.status === 'PENDING' || currentApproval.status === 'REJECTED';
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'badge-pending',
      APPROVED: 'badge-approved',
      REJECTED: 'badge-rejected'
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const nocTypeLabels = {
    TRANSFER_CERTIFICATE: 'Transfer Certificate',
    COURSE_COMPLETION: 'Course Completion',
    INTERNSHIP: 'Internship',
    SCHOLARSHIP: 'Scholarship',
    EXAM_HALL_TICKET: 'Exam Hall Ticket',
    LIBRARY_CLEARANCE: 'Library Clearance',
    FEE_CLEARANCE: 'Fee Clearance',
    OTHER: 'Other'
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!nocRequest) {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/noc')}
          className="mb-6 text-slate-600 hover:text-slate-900 flex items-center gap-2"
        >
          ← Back to NOC Requests
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {nocTypeLabels[nocRequest.nocType]}
              </h1>
              <p className="text-slate-600">
                Request ID: {nocRequest.id.slice(0, 8)}...
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(nocRequest.overallStatus)}`}>
              {nocRequest.overallStatus}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Student Name</label>
              <p className="text-lg text-slate-900">{nocRequest.student.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">USN</label>
              <p className="text-lg text-slate-900">{nocRequest.student.usn}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Department</label>
              <p className="text-lg text-slate-900">{nocRequest.student.department}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Submitted On</label>
              <p className="text-lg text-slate-900">
                {new Date(nocRequest.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-600 mb-2">Reason</label>
            <p className="text-slate-900 bg-slate-50 rounded-lg p-4">
              {nocRequest.reason}
            </p>
          </div>

          {nocRequest.additionalDetails && (
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">Additional Details</label>
              <p className="text-slate-900 bg-slate-50 rounded-lg p-4">
                {nocRequest.additionalDetails}
              </p>
            </div>
          )}
        </div>

        {/* Approval Timeline */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Approval Status</h2>
          
          {/* Staff Action Buttons - Show if staff can take action */}
          {user?.role !== 'STUDENT' && canModifyApproval() && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              {getCurrentUserApproval()?.status === 'REJECTED' ? (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">⚠️</span>
                    <p className="text-sm text-orange-800 font-medium">
                      You previously rejected this request. You can modify your decision below.
                    </p>
                  </div>
                  {getCurrentUserApproval()?.remarks && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded mb-3">
                      <p className="text-sm text-red-900">
                        <strong>Your previous rejection reason:</strong> {getCurrentUserApproval().remarks}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-blue-800 mb-4">
                  <strong>Action Required:</strong> This request is pending your approval.
                </p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const remarks = prompt(
                      getCurrentUserApproval()?.status === 'REJECTED' 
                        ? 'Update remarks (optional):' 
                        : 'Add remarks (optional):'
                    );
                    if (remarks !== null) {
                      handleApproval('APPROVED', remarks);
                    }
                  }}
                  disabled={processingApproval}
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingApproval ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                      Processing...
                    </span>
                  ) : (
                    getCurrentUserApproval()?.status === 'REJECTED' ? '✓ Approve Now' : '✓ Approve'
                  )}
                </button>
                
                {getCurrentUserApproval()?.status !== 'REJECTED' && (
                  <button
                    onClick={() => {
                      const remarks = prompt('Reason for rejection (required):');
                      if (remarks && remarks.trim()) {
                        handleApproval('REJECTED', remarks);
                      } else if (remarks !== null) {
                        toast.error('Remarks are required for rejection');
                      }
                    }}
                    disabled={processingApproval}
                    className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ✗ Reject
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {(() => {
              const approvalOrder = ['ACCOUNTS_SECTION', 'LIBRARY_STAFF', 'HOD', 'EXAM_SECTION'];
              const sortedApprovals = [...nocRequest.approvals].sort((a, b) => 
                approvalOrder.indexOf(a.approverRole) - approvalOrder.indexOf(b.approverRole)
              );
              return sortedApprovals.map((approval, index) => {
                const isCurrentUser = user?.role === approval.approverRole;
                const approverName = approval.approver?.name;
                return (
                  <div
                    key={approval.id}
                    className={`border-l-4 pl-6 py-4 ${
                      approval.status === 'APPROVED'
                        ? 'border-emerald-500 bg-emerald-50'
                        : approval.status === 'REJECTED'
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-300 bg-slate-50'
                    } ${isCurrentUser && approval.status === 'REJECTED' ? 'ring-2 ring-orange-300' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-600 mr-1">{index + 1}.</span>
                        <h3 className="font-semibold text-slate-900">
                          {approval.approverRole.replace('_', ' ')}
                        </h3>
                        {approverName && approval.status !== 'PENDING' && (
                          <span className="text-xs bg-slate-200 text-slate-800 px-2 py-1 rounded-full ml-2">
                            {approverName}
                          </span>
                        )}
                        {isCurrentUser && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                            You
                          </span>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(approval.status)}`}>
                        {approval.status}
                      </span>
                    </div>

                    {approval.status !== 'PENDING' && (
                      <>
                        <p className="text-sm text-slate-600 mb-2">
                          {approval.status === 'APPROVED' ? 'Approved' : 'Rejected'} on{' '}
                          {new Date(approval.approvedAt || approval.rejectedAt).toLocaleDateString()}
                        </p>
                        {approval.remarks && (
                          <p className="text-sm text-slate-700 italic">
                            Remarks: {approval.remarks}
                          </p>
                        )}
                      </>
                    )}
                    
                    {isCurrentUser && approval.status === 'REJECTED' && (
                      <p className="text-xs text-orange-600 mt-2 font-medium">
                        💡 You can change your decision by clicking "Approve Now" above
                      </p>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Download NOC */}
        {nocRequest.overallStatus === 'APPROVED' && nocRequest.finalNOCPath && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="text-lg font-semibold text-emerald-900 mb-2">
              Your NOC has been approved!
            </h3>
            <p className="text-emerald-800 mb-4">
              Download your No Objection Certificate below
            </p>
            <button
              onClick={handleDownloadNOC}
              className="inline-block px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              📄 Download NOC Certificate
            </button>
          </div>
        )}

        {nocRequest.overallStatus === 'REJECTED' && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">❌</div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">
                {user?.role === 'STUDENT' 
                  ? 'Your NOC request was rejected' 
                  : 'This NOC request was rejected'}
              </h3>
            </div>
            
            {user?.role === 'STUDENT' ? (
              <div className="bg-white rounded-lg p-5 mb-4">
                <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                  <span className="text-xl">📋</span>
                  What should you do next?
                </h4>
                <ol className="list-decimal list-inside space-y-3 text-sm text-slate-800">
                  <li className="pl-2">
                    <strong>Review the rejection reason</strong> in the approval timeline above to understand why it was rejected.
                  </li>
                  <li className="pl-2">
                    <strong>Contact the department</strong> that rejected your request (shown in the approval timeline) to discuss the concerns.
                  </li>
                  <li className="pl-2">
                    <strong>Provide additional documentation or clarification</strong> if requested by the department.
                  </li>
                  <li className="pl-2">
                    <strong>The approver can reconsider</strong> and change their decision to approve your request.
                  </li>
                </ol>
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-900 flex items-start gap-2">
                    <span className="text-lg">✅</span>
                    <span>
                      <strong>Good News:</strong> You do NOT need to submit a new NOC request. 
                      The approver can modify their decision on this same request once your concerns are addressed.
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-red-800 text-center">
                You can modify your rejection decision above if the student has addressed the concerns.
              </p>
            )}
            
            {user?.role === 'STUDENT' && (
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-3">
                  Need help? Contact the department office or visit the administrative section.
                </p>
                <button
                  onClick={() => navigate('/noc')}
                  className="inline-block px-5 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors text-sm"
                >
                  ← Back to My Requests
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NOCDetail;
